import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { map } from 'lodash';
import PropTypes from 'prop-types';

import { AccountsTable } from 'src/components/AccountsTable';
import { AuthErrorMessage } from 'src/components/AuthErrorMessage';
import { ChannelIcon } from 'src/components/ChannelIcon';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import {
  CHANNEL,
  EXTERNAL_CHANNELS,
  getChannelPerformanceLabel,
  INTEGRATION_CHANNELS,
} from 'src/data/channel';
import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { EMAILS_CHANNELS_AND_RESOURCES } from 'src/data/performance';
import { useChannelAccounts, useCheckChannelConnected, useUserChannelAccounts } from 'src/services';
import { useCustomerIntegrationsReAuthEnabled } from 'src/services/customer/useCustomerIntegrationsReAuthEnabled';
import { useCustomFeatureLimitsData, usePerformanceActions } from 'src/store';
import { FeatureLimitedProgressBar, RowsUsageHeader } from 'src/views/FeatureLimitedProgressBar';

import { ActionsMenu } from './components/ActionsMenu';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

const getTitle = (channel, isTrainingModel) => {
  if (isTrainingModel) {
    return 'Train a custom model';
  } else {
    return `Select ${getChannelPerformanceLabel(channel)} ${
      getChannelPerformanceLabel(channel).endsWith('account') ? '' : 'account'
    } to integrate with Anyword`;
  }
};

export const AdAccountsSelectionPopup = ({
  open,
  channel,
  onClose,
  isTrainingModel,
  brandVoiceId,
  customModel,
  onConnectSuccess,
}) => {
  const styles = useStyles();
  const performanceActions = usePerformanceActions();

  const [refreshError, setRefreshError] = useState(null);

  const {
    isLoadingUserAccounts,
    isLoadingWorkspaceAccounts,
    accountsSortField,
    accountsSortDirection,
    connectingToChannel,
  } = useViewState(channel);

  useEffect(() => {
    if (!open) return;
    setRefreshError(null);
    performanceActions.refreshIntegrationAssets(channel).catch((error) => {
      setRefreshError(error?.message || 'Connection expired');
    });
  }, [open, channel, performanceActions]);
  const customerReAuthEnabled = useCustomerIntegrationsReAuthEnabled();
  const { limit, currentCount, limitedAccountData } = useCustomFeatureLimitsData(
    FEATURE_LIMITS.resourcesRowsPerCustomer
  );
  const limitData = { limit, currentCount };
  const isFeatureLimited = limit && (!!limitedAccountData?.accountId || limit <= currentCount);

  const checkIsChannelConnected = useCheckChannelConnected();

  const isChannelConnected = checkIsChannelConnected(channel);

  const { handleSortChange, handleReauthorize, handleDisconnect, handleAccountClick } = useHandlers(
    {
      channel,
      onConnectSuccess,
      onClose,
      isTrainingModel,
      brandVoiceId,
      isFeatureLimited,
      customModel,
    }
  );
  const { connectedAccounts } = useChannelAccounts();
  const { disconnectedAccounts } = useUserChannelAccounts();

  const channelConnectedAccounts = useMemo(() => {
    if (channel === CHANNEL.emailChannel) {
      const connectedFileResources = EMAILS_CHANNELS_AND_RESOURCES.reduce((acc, channel) => {
        const resources = connectedAccounts[channel] || [];
        return [...acc, ...resources];
      }, []);
      return [...(connectedAccounts?.[channel] || []), ...connectedFileResources];
    }

    return connectedAccounts?.[channel] || [];
  }, [connectedAccounts, channel]);

  const channelDisconnectedAccounts = useMemo(() => {
    const channelConnectedAccountIds = map(channelConnectedAccounts, 'id');

    return (disconnectedAccounts?.[channel] || []).filter(
      (disconnectedAccount) => !channelConnectedAccountIds.includes(disconnectedAccount.id)
    );
  }, [disconnectedAccounts, channel, channelConnectedAccounts]);

  const title = getTitle(channel, isTrainingModel);
  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={onClose}
      maxWidth="md"
      data-testid="AdAccountsSelectionPopup"
    >
      <DialogTitle
        className={styles.dialogTitle}
        disableTypography
      >
        <CustomTypography
          className={styles.popupTitle}
          variant={TYPOGRAPHY_VARIANT.h3}
        >
          {!isTrainingModel && (
            <ChannelIcon
              className={styles.channelIcon}
              channel={channel}
            />
          )}
          {title}
        </CustomTypography>

        {!isTrainingModel && (
          <ActionsMenu
            onReauthorizeClick={handleReauthorize}
            onRemoveClick={handleDisconnect}
            displayReAuth={customerReAuthEnabled}
          />
        )}
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {refreshError && (
          <AuthErrorMessage
            channel={channel}
            onConnectToChannel={handleReauthorize}
          />
        )}
        {isTrainingModel && (
          <CustomTypography
            variant={TYPOGRAPHY_VARIANT.paragraph16}
            className={styles.customModelSubtitle}
          >
            Select the data you want train the model on
          </CustomTypography>
        )}
        <AccountsTable
          accounts={channelDisconnectedAccounts}
          connectedAccounts={channelConnectedAccounts}
          anyAccountConnected={channelConnectedAccounts.length > 0}
          onAccountClick={handleAccountClick}
          onSortChange={handleSortChange}
          sortField={accountsSortField}
          sortDirection={accountsSortDirection}
          channel={channel}
          limitedAccountData={limitedAccountData}
          loading={isLoadingUserAccounts || isLoadingWorkspaceAccounts || connectingToChannel}
          isTrainingModel={isTrainingModel}
          isChannelConnected={isChannelConnected}
        />
      </DialogContent>
      <DialogActions>
        {isFeatureLimited && (
          <FeatureLimitedProgressBar
            feature={FEATURE_LIMITS.resourcesRowsPerCustomer}
            limitData={limitData}
            upgradeActionOrigin={UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog}
            headerLabel={
              <RowsUsageHeader
                connectedAccounts={connectedAccounts}
                limitData={limitData}
              />
            }
          />
        )}
        <Button
          className={styles.buttonCancel}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="AddAccountSelectionPopup__CancelButton"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AdAccountsSelectionPopup.propTypes = {
  open: PropTypes.bool,
  channel: PropTypes.oneOf([...INTEGRATION_CHANNELS, ...EXTERNAL_CHANNELS]).isRequired,
  onClose: PropTypes.func.isRequired,
  isTrainingModel: PropTypes.bool,
  brandVoiceId: PropTypes.string,
  customModel: PropTypes.shape({}),
  onConnectSuccess: PropTypes.func,
};
AdAccountsSelectionPopup.defaultProps = {
  open: false,
  isTrainingModel: false,
  brandVoiceId: null,
  onConnectSuccess: null,
};
